import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as ApplicationIconActive } from "../../assets/images/icons/ic-applications-active.svg";
import { ReactComponent as ApplicationIcon } from "../../assets/images/icons/ic-applications.svg";
import { ReactComponent as ArticleIconActive } from "../../assets/images/icons/ic-article-active.svg";
import { ReactComponent as ArticleIcon } from "../../assets/images/icons/ic-article.svg";
import { ReactComponent as BookIconActive } from "../../assets/images/icons/ic-book-active.svg";
import { ReactComponent as BookIcon } from "../../assets/images/icons/ic-book-inactive.svg";
import { ReactComponent as EventsActiveIcon } from "../../assets/images/icons/ic-calendar-star-filled.svg";
import { ReactComponent as EventsIcon } from "../../assets/images/icons/ic-calendar-star.svg";
import { ReactComponent as CareersIconActive } from "../../assets/images/icons/ic-careers-active.svg";
import { ReactComponent as CareersIcon } from "../../assets/images/icons/ic-careers.svg";
import { ReactComponent as CartIcon } from "../../assets/images/icons/ic-cart-inactive.svg";
import { ReactComponent as CartIconActive } from "../../assets/images/icons/ic-cart.svg";
import { ReactComponent as ChatIconActive } from "../../assets/images/icons/ic-chat-active.svg";
import { ReactComponent as ChatIcon } from "../../assets/images/icons/ic-chat.svg";
import { ReactComponent as ChevronIcon } from "../../assets/images/icons/ic-chevron.svg";
import { ReactComponent as CommunityIconActive } from "../../assets/images/icons/ic-community-active.svg";
import { ReactComponent as CommunityIcon } from "../../assets/images/icons/ic-community.svg";
import { ReactComponent as ContentIconActive } from "../../assets/images/icons/ic-content-active.svg";
import { ReactComponent as ContentIcon } from "../../assets/images/icons/ic-content.svg";
import { ReactComponent as CouponIconActive } from "../../assets/images/icons/ic-coupon-active.svg";
import { ReactComponent as CouponIcon } from "../../assets/images/icons/ic-coupon.svg";
import { ReactComponent as CodeIcon } from "../../assets/images/icons/ic-code.svg";
import { ReactComponent as DashboardIconActive } from "../../assets/images/icons/ic-dashboard-active.svg";
import { ReactComponent as DashboardIcon } from "../../assets/images/icons/ic-dashboard-inactive.svg";
import { ReactComponent as DocumentIcon } from "../../assets/images/icons/ic-document.svg";
import { ReactComponent as DocumentIconOutlined } from "../../assets/images/icons/ic-document-outlined.svg";
import { ReactComponent as ExerciseIconActive } from "../../assets/images/icons/ic-exercise-active.svg";
import { ReactComponent as ExerciseIcon } from "../../assets/images/icons/ic-exercise-inactive.svg";
import { ReactComponent as FeedbackIconActive } from "../../assets/images/icons/ic-feedback-active.svg";
import { ReactComponent as FeedbackIcon } from "../../assets/images/icons/ic-feedback.svg";
import { ReactComponent as MaterialsIconActive } from "../../assets/images/icons/ic-files-active.svg";
import { ReactComponent as MaterialsIcon } from "../../assets/images/icons/ic-files-inactive.svg";
import { ReactComponent as LessonsIconActive } from "../../assets/images/icons/ic-lessons-active.svg";
import { ReactComponent as LessonsIcon } from "../../assets/images/icons/ic-lessons-inactive.svg";
import { ReactComponent as MacroIconActive } from "../../assets/images/icons/ic-macro-active.svg";
import { ReactComponent as MacroIcon } from "../../assets/images/icons/ic-macro-inactive.svg";
import { ReactComponent as MailIconActive } from "../../assets/images/icons/ic-mail-active.svg";
import { ReactComponent as MailIcon } from "../../assets/images/icons/ic-mail.svg";
import { ReactComponent as CommunicationsIconActive } from "../../assets/images/icons/ic-megaphone-active.svg";
import { ReactComponent as CommunicationsIcon } from "../../assets/images/icons/ic-megaphone.svg";
import { ReactComponent as MoneyIconActive } from "../../assets/images/icons/ic-money-paper-active.svg";
import { ReactComponent as MoneyIcon } from "../../assets/images/icons/ic-money-paper.svg";
import { ReactComponent as BellFillIcon } from "../../assets/images/icons/ic-notification-active.svg";
import { ReactComponent as BellIcon } from "../../assets/images/icons/ic-notification.svg";
import { ReactComponent as SettingsIcon } from "../../assets/images/icons/ic-settings-inactive.svg";
import { ReactComponent as SettingsIconActive } from "../../assets/images/icons/ic-settings.svg";
import { ReactComponent as ShopIconActive } from "../../assets/images/icons/ic-shop-active.svg";
import { ReactComponent as ShopIcon } from "../../assets/images/icons/ic-shop.svg";
import { ReactComponent as StatsActiveIcon } from "../../assets/images/icons/ic-stats-active.svg";
import { ReactComponent as StatsIcon } from "../../assets/images/icons/ic-stats.svg";
import { ReactComponent as SurveyIconActive } from "../../assets/images/icons/ic-survey-active.svg";
import { ReactComponent as SurveyIcon } from "../../assets/images/icons/ic-survey.svg";
import { ReactComponent as TargetIconActive } from "../../assets/images/icons/ic-target-active.svg";
import { ReactComponent as TargetIcon } from "../../assets/images/icons/ic-target.svg";
import { ReactComponent as TeachingIconActive } from "../../assets/images/icons/ic-teaching-active.svg";
import { ReactComponent as TeachingIcon } from "../../assets/images/icons/ic-teaching.svg";
import { ReactComponent as TestIconActive } from "../../assets/images/icons/ic-test-active.svg";
import { ReactComponent as TestIcon } from "../../assets/images/icons/ic-test-inactive.svg";
import { ReactComponent as ReviewIconActive } from "../../assets/images/icons/ic-thumb-up-fill.svg";
import { ReactComponent as ReviewIcon } from "../../assets/images/icons/ic-thumb-up.svg";
import { ReactComponent as VideoIconActive } from "../../assets/images/icons/ic-video-active.svg";
import { ReactComponent as VideoIcon } from "../../assets/images/icons/ic-video.svg";
import { ReactComponent as BotIcon } from "../../assets/images/icons/ic-bot.svg";
import { ReactComponent as BotIconFilled } from "../../assets/images/icons/ic-bot-filled.svg";

import UserPlaceholder from "../../assets/images/placeholders/user-placeholder.png";
import { ReactComponent as Logo } from "../../assets/images/starting-finance.svg";
import { ReactComponent as LogoMini } from "../../assets/images/starting-finance-minimized.svg";
import { AdminRole, SHOW_NOTIFICATIONS } from "../../common/constants";
import { canAccessPage, navSectionsConfig } from "../../common/permissions";
import { Each } from "../../common/Each";
import useWindowDimensions from "../../common/hooks/useWindowDimensions";
import MainContext from "../../common/MainContext";
import { useWebSocket } from "../../common/WebSocketContext";
import SimpleCollapsable from '../../components/SimpleCollapsable';
import Skeleton from "../Skeleton";
import Hamburger from "./Hamburger";
import styles from "./SideNav.module.css";

const SideNav = ({ onChange }) => {

    const { websocket } = useWebSocket()
    const { t } = useTranslation()
    const { width } = useWindowDimensions()
    const location = useLocation()
    const context = useContext(MainContext)

    const [expanded, setExpanded] = useState({
        content: false,
        teaching: false,
        shop: false,
        reports: false,
    })

    const [active, setActive] = useState({
        content: false,
        teaching: false,
        shop: false,
        reports: false,
    })

    const navSections = useMemo(() => navSectionsConfig.map(section => section.id === 'content' ? { ...section, children: section.children.filter(page => page.id === 'videos') } : section), [])

    const navigate = useNavigate()

    const isCompanyAdmin = Boolean(
        context.user?.company_id ?? context.user?.company?.id ?? context.user?.company
    )

    const [isOpen, setIsOpen] = useState(false)
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const isMobile = width <= 768

    useEffect(() => { setIsOpen(false) }, [isMobile])
    useEffect(() => { onChangeRef.current?.(isOpen) }, [isOpen])

    const noSideNavRoutes = useMemo(() => { return ["/login", "/actions", "/privacy-policy", "/terms-and-conditions", "/events-conditions", "/macroPreview", "/changePassword"] }, [])

    useEffect(() => {
        if (context.scrollRef?.current) {
            context.scrollRef?.current.scrollIntoView()
        }
    }, [location])

    useEffect(() => {
        if (location.pathname !== "/changePassword" && context?.user?.force_change_password) {
            navigate("/changePassword")
        }
        for (const navSection of navSections.filter(n => n.children && n.children.length > 0)) {
            let isActive = false
            for (const { id } of navSection.children) {
                if (
                    location.pathname === `/${id}` ||
                    location.pathname.startsWith(`/${id}/`)
                ) {
                    isActive = true
                    break;
                }
            }
            setExpanded((e) => ({ ...e, [navSection.id]: isActive }))
            setActive((a) => ({ ...a, [navSection.id]: isActive }))
        }
    }, [location, context.user?.force_change_password, navigate, navSections])

    useEffect(() => {
        if (!websocket) {
            return;
        }

        let titleEl = document.querySelector("title");
        let retryTimer = null;

        const update = () => {
            const socketReady = websocket?.socket?.readyState === WebSocket.OPEN;

            if (!socketReady) {
                console.warn('websocket non pronto, riprovo...')
                retryTimer = setTimeout(update, 1000);
                return;
            }

            try {
                websocket.sendMessage({
                    type: 'analytics.page_view',
                    page: window.location.pathname,
                    title: titleEl?.textContent ?? 'Dashboard',
                })
            } catch (err) {
                console.error('errore invio messaggio websocket', err)
            }
        }

        // osserva il contenuto del <title> (text node)
        const titleObserver = new MutationObserver(update);

        // osserva <head> per rimpiazzi del <title>
        const headObserver = new MutationObserver(() => {
            const newTitleEl = document.querySelector("title");
            if (newTitleEl && newTitleEl !== titleEl) {
                titleObserver.disconnect();
                titleEl = newTitleEl;
                // ascolta sia cambi di figli che del testo
                titleObserver.observe(titleEl, {
                    childList: true,
                    characterData: true,
                    subtree: true,
                });
                update();
            }
        });

        if (titleEl) {
            titleObserver.observe(titleEl, {
                childList: true,
                characterData: true,
                subtree: true,
            });
        }

        headObserver.observe(document.head, { childList: true, subtree: true });
        update();

        return () => {
            titleObserver.disconnect();
            headObserver.disconnect();
            if (retryTimer) clearTimeout(retryTimer);
            document.body.style.overflow = '';
        }
    }, [websocket])



    useEffect(() => {
        if (!isOpen || !isMobile) return
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = previousOverflow }
    }, [isOpen, isMobile])

    function closeSideNav() {
        if (width <= 768) {
            setIsOpen(false)
        }
    }

    function getIcon(page) {
        if (page === "" && location.pathname === "/") {
            return <DashboardIconActive style={{ width: '20px', height: '20px' }}></DashboardIconActive>
        } else if (page === "") {
            return <DashboardIcon style={{ width: '20px', height: '20px' }}></DashboardIcon>
        }

        switch (page) {
            case "content": return active.content ?
                <ContentIconActive style={{ width: '20px', height: '20px' }} /> :
                <ContentIcon style={{ width: '20px', height: '20px' }} />
            case "shop": return active.shop ?
                <ShopIconActive style={{ width: '20px', height: '20px' }} /> :
                <ShopIcon style={{ width: '20px', height: '20px' }} />
            case "teaching": return active.teaching ?
                <TeachingIconActive style={{ width: '20px', height: '20px' }} /> :
                <TeachingIcon style={{ width: '20px', height: '20px' }} />
            case 'reports':
                return active.reports ?
                    <StatsActiveIcon style={{ width: '20px', height: '20px' }} /> :
                    <StatsIcon style={{ width: '20px', height: '20px' }} />
            default: break;
        }

        const pagePath = page === 'course-quiz-campaigns' ? '/campaigns' : `/${page}`
        const isActive = location.pathname && (location.pathname === pagePath || location.pathname.startsWith(`${pagePath}/`))
        switch (page) {
            case 'macros':
                return isActive ?
                    <MacroIconActive style={{ width: '20px', height: '20px' }} /> :
                    <MacroIcon style={{ width: '20px', height: '20px' }} />
            case 'careers':
                return isActive ?
                    <CareersIconActive style={{ width: '20px', height: '20px' }} /> :
                    <CareersIcon style={{ width: '20px', height: '20px' }} />
            case 'courses':
                return isActive ?
                    <BookIconActive style={{ width: '20px', height: '20px' }} /> :
                    <BookIcon style={{ width: '20px', height: '20px' }} />
            case 'shop_courses':
                return isActive ?
                    <BookIconActive style={{ width: '20px', height: '20px' }} /> :
                    <BookIcon style={{ width: '20px', height: '20px' }} />
            case 'users':
                return isActive ?
                    <CommunityIconActive style={{ width: '20px', height: '20px' }} /> :
                    <CommunityIcon style={{ width: '20px', height: '20px' }} />
            case 'settings':
                return isActive ?
                    <SettingsIconActive style={{ width: '20px', height: '20px' }} /> :
                    <SettingsIcon style={{ width: '20px', height: '20px' }} />
            case 'orders':
                return isActive ?
                    <CartIconActive style={{ width: '20px', height: '20px' }} /> :
                    <CartIcon style={{ width: '20px', height: '20px' }} />
            case 'videos':
                return isActive ?
                    <VideoIconActive style={{ width: '20px', height: '20px' }} /> :
                    <VideoIcon style={{ width: '20px', height: '20px' }} />
            case 'downloads':
                return isActive ?
                    <MaterialsIconActive style={{ width: '20px', height: '20px' }} /> :
                    <MaterialsIcon style={{ width: '20px', height: '20px' }} />
            case 'articles':
                return isActive ?
                    <ArticleIconActive style={{ width: '20px', height: '20px' }} /> :
                    <ArticleIcon style={{ width: '20px', height: '20px' }} />
            case 'reviews':
                return isActive ?
                    <ReviewIconActive style={{ width: '20px', height: '20px' }} /> :
                    <ReviewIcon style={{ width: '20px', height: '20px' }} />
            // case 'bookings':
            //     return isActive ?
            //         <BookingsActiveIcon style={{ width: '20px', height: '20px' }} /> :
            //         <BookingsIcon style={{ width: '20px', height: '20px' }} />
            case 'exercises':
                return isActive ?
                    <ExerciseIconActive style={{ width: '20px', height: '20px' }} /> :
                    <ExerciseIcon style={{ width: '20px', height: '20px' }} />
            case 'tests':
                return isActive ?
                    <TestIconActive style={{ width: '20px', height: '20px' }} /> :
                    <TestIcon style={{ width: '20px', height: '20px' }} />
            case 'materials':
                return isActive ?
                    <MaterialsIconActive style={{ width: '20px', height: '20px' }} /> :
                    <MaterialsIcon style={{ width: '20px', height: '20px' }} />
            case 'messages':
                return isActive ?
                    <ChatIconActive style={{ width: '20px', height: '20px' }} /> :
                    <ChatIcon style={{ width: '20px', height: '20px' }} />
            case 'course-quiz-campaigns':
                return isActive ?
                    <CommunicationsIconActive style={{ width: '20px', height: '20px' }} /> :
                    <CommunicationsIcon style={{ width: '20px', height: '20px' }} />
            case 'surveys':
                return isActive ?
                    <SurveyIconActive style={{ width: '20px', height: '20px' }} /> :
                    <SurveyIcon style={{ width: '20px', height: '20px' }} />
            case 'applications':
                return isActive ?
                    <ApplicationIconActive style={{ width: '20px', height: '20px' }} /> :
                    <ApplicationIcon style={{ width: '20px', height: '20px' }} />
            case 'communications':
                return isActive ?
                    <CommunicationsIconActive style={{ width: '20px', height: '20px' }} /> :
                    <CommunicationsIcon style={{ width: '20px', height: '20px' }} />
            case 'feedbacks':
                return isActive ?
                    <FeedbackIconActive style={{ width: '20px', height: '20px' }} /> :
                    <FeedbackIcon style={{ width: '20px', height: '20px' }} />
            case 'coupons':
                return isActive ?
                    <CouponIconActive style={{ width: '20px', height: '20px' }} /> :
                    <CouponIcon style={{ width: '20px', height: '20px' }} />
            case 'invoices':
                return isActive ?
                    <DocumentIcon style={{ width: '20px', height: '20px' }} /> :
                    <DocumentIconOutlined style={{ width: '20px', height: '20px' }} />
            case 'reports/sales':
                return isActive ?
                    <MoneyIconActive style={{ width: '20px', height: '20px' }} /> :
                    <MoneyIcon style={{ width: '20px', height: '20px' }} />
            case 'reports/teaching':
                return isActive ?
                    <TeachingIconActive style={{ width: '20px', height: '20px' }} /> :
                    <TeachingIcon style={{ width: '20px', height: '20px' }} />
            case 'reports/orders':
                return isActive ?
                    <CartIconActive style={{ width: '20px', height: '20px' }} /> :
                    <CartIcon style={{ width: '20px', height: '20px' }} />
            case 'reports/marketing':
                return isActive ?
                    <TargetIconActive style={{ width: '20px', height: '20px' }} /> :
                    <TargetIcon style={{ width: '20px', height: '20px' }} />
            case 'forms':
                return isActive ? <DocumentIcon style={{ width: '20px', height: '20px' }} /> : <DocumentIconOutlined style={{ width: '20px', height: '20px' }} />
            case 'mailings':
                return isActive ?
                    <MailIconActive style={{ width: '20px', height: '20px' }} /> :
                    <MailIcon style={{ width: '20px', height: '20px' }} />
            case 'lessons':
                return isActive ?
                    <LessonsIconActive style={{ width: '20px', height: '20px' }} /> :
                    <LessonsIcon style={{ width: '20px', height: '20px' }} />
            case 'events':
                return isActive ?
                    <EventsActiveIcon style={{ width: '20px', height: '20px' }} /> :
                    <EventsIcon style={{ width: '20px', height: '20px' }} />
            case 'jobs':                return isActive ?
                    <BotIconFilled style={{ width: '20px', height: '20px' }} /> :
                    <BotIcon style={{ width: '20px', height: '20px' }} />
            case 'developers':
                return <CodeIcon style={{ width: '20px', height: '20px' }} />
            default: return
        }
    }

    function navigateTo(page) {
        if (location.pathname.includes("/lessons/")) {
            window.location.href = page
        }
        else {
            navigate(page)
        }
    }

    const canViewNavPage = (pageId, fallbackPermissionLevel) => {
        return canAccessPage(context.user, pageId, 'view', fallbackPermissionLevel)
    }

    const canViewSection = (section) => {
        if (!section.children || section.children.length === 0) {
            return canViewNavPage(section.id, section.permissionLevel)
        }

        if (!canViewNavPage(section.id, section.permissionLevel)) {
            return section.children.some((child) => canViewNavPage(child.id, child.permissionLevel))
        }

        return true
    }

    return (
        <>
            {
                noSideNavRoutes.filter(s => location.pathname.includes(s)).length === 0 && (
                    <div className={styles.rail} id="sidenav-rail">
                    <nav aria-label="Menu principale" onMouseEnter={() => { if (!isMobile) setIsOpen(true) }} onMouseLeave={(event) => { if (!isMobile && !event.currentTarget.contains(document.activeElement)) setIsOpen(false) }} onFocusCapture={() => { if (!isMobile) setIsOpen(true) }} onBlurCapture={(event) => { if (!isMobile && !event.currentTarget.contains(event.relatedTarget)) setIsOpen(false) }} onKeyDown={(event) => { if (event.key === "Escape") setIsOpen(false) }} className={styles.sidenav + ' ' + (isOpen ? styles.open : styles.close)} id="sidenav">
                        <div className={styles.header}>

                            {width > 768 &&
                                <LogoMini className={styles.logoMini}></LogoMini>
                            }

                            <Logo className={styles.logo} />
                            <div className={styles.spacer} />

                            {width <= 768 &&
                                <>
                                    {SHOW_NOTIFICATIONS && <div className={styles.iconButtons}>
                                        <button aria-label="Notifiche" className={styles.iconButton} onClick={() => {
                                            closeSideNav()
                                            navigate("/notifications")
                                        }}>
                                            {context.notifications?.metadata.unread === 0 &&
                                                <BellIcon />
                                            }
                                            {context.notifications?.metadata.unread > 0 &&
                                                <>
                                                    <BellFillIcon />
                                                    <div className={styles.badge}>
                                                        {context.notifications.metadata.unread}
                                                    </div>
                                                </>
                                            }
                                        </button>
                                    </div>}
                                    <div className={styles.hamburger}>
                                        <Hamburger isOpen={isOpen} onChange={(event) => { setIsOpen(event.target.checked) }}></Hamburger>
                                    </div>
                                </>
                            }


                        </div>
                        <div className={styles.content}>
                            <button aria-label="Dashboard" className={[styles.navLink, location.pathname === '/' ? styles.active : ''].join(' ')}
                                onClick={() => {
                                    closeSideNav()
                                    navigateTo('/')
                                }}>
                                {getIcon('')}
                                <span className={styles.navLabel}>Dashboard</span>
                            </button>
                            <Each
                                of={navSections.filter((n) => canViewSection(n))}
                                render={(item) => (
                                    <>
                                        {
                                            !item.children &&
                                            <a href={item.path ?? `/${item.id}`} rel="noreferrer" style={{ textDecoration: 'none' }} onClick={(e) => { e.preventDefault() }}>
                                                <button
                                                    aria-label={t(`sidenav.${item.id}`)}
                                                    className={location.pathname.includes(item.path ?? `/${item.id}`) ? `${styles.navLink} ${styles.active}` : styles.navLink}
                                                    onClick={() => {
                                                        closeSideNav()
                                                        navigateTo(item.path ?? `/${item.id}`)
                                                    }}>
                                                    {getIcon(item.id)}
                                                    <span className={styles.navLabel}>{t(`sidenav.${item.id}`)}</span>
                                                </button>
                                            </a>
                                        }
                                        {
                                            item.children && item.children.length > 0 &&
                                            <>

                                                <button
                                                    aria-label={t(`sidenav.${item.id}`)}
                                                    aria-expanded={isOpen && Boolean(expanded[item.id])}
                                                    onClick={() => setExpanded(e => ({ ...e, [item.id]: !e[item.id] }))}
                                                    className={active[item.id] ? `${styles.navLink} ${styles.sectionActive}` : styles.navLink}
                                                >
                                                    {getIcon(item.id)}
                                                    <span className={styles.navLabel}>{t(`sidenav.${item.id}`)}
                                                            <div className={styles.chevron}>
                                                                <ChevronIcon
                                                                    style={{
                                                                        transform: !expanded[item.id] ? "rotate(180deg)" : "none",
                                                                    }} />
                                                            </div></span>
                                                </button>
                                                <SimpleCollapsable expanded={isOpen && expanded[item.id]}>
                                                    <div className={styles.subNavLinks} hidden={!isOpen || !expanded[item.id]}>
                                                        <Each
                                                            of={item.children}
                                                            render={(child) => {
                                                                if (canViewNavPage(child.id, child.permissionLevel)) {
                                                                    return (
                                                                        <a href={`/${child.id}`} rel="noreferrer" style={{ textDecoration: 'none' }} onClick={(e) => { e.preventDefault() }}>
                                                                            <button aria-label={t(`sidenav.${child.id}`)} className={[styles.navLink, location.pathname.includes(child.id) ? styles.active : ''].join(' ')}
                                                                                onClick={() => {
                                                                                    closeSideNav()
                                                                                    navigateTo(`/${child.id}`)
                                                                                }}>
                                                                                {getIcon(child.id)}
                                                                                <span className={styles.navLabel}>{t(`sidenav.${child.id}`)}</span>
                                                                            </button>
                                                                        </a>
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </SimpleCollapsable>
                                            </>
                                        }
                                    </>
                                )}
                            />
                        </div>
                        {/* <div className={styles.spacer}></div> */}
                        <div className={styles.bottom}>
                            {SHOW_NOTIFICATIONS && <button aria-label="Notifiche" className={[styles.bottomNavLink, location.pathname === '/notifications' ? styles.active : ''].join(' ')} onClick={() => { closeSideNav(); navigateTo('/notifications') }}>
                                <BellIcon style={{ width: 20, height: 20 }} />
                                <span className={styles.navLabel}>Notifiche</span>
                            </button>}
                            {context.user?.role === AdminRole.Main && !isCompanyAdmin &&
                                <a href="/messages" rel="noreferrer" style={{ textDecoration: 'none' }} onClick={(e) => { e.preventDefault() }}>
                                    <button
                                        aria-label={t("sidenav.messages")}
                                        className={[styles.bottomNavLink, location.pathname.includes('/messages') ? styles.active : ''].join(' ')}
                                        onClick={() => {
                                            closeSideNav()
                                            navigateTo('/messages')
                                        }}>
                                        {getIcon('messages', context.un)}
                                        {context.unreadMessages > 0 &&
                                            <div className={styles.iconBadge}>{context.unreadMessages}</div>
                                        }
                                        <span className={styles.navLabel}>{t('sidenav.messages')}</span>
                                    </button>
                                </a>
                            }
                            <a href="/profile" rel="noreferrer" style={{ textDecoration: 'none' }} onClick={(e) => { e.preventDefault() }}>
                                <button aria-label="Profilo" className={styles.userButton} onClick={() => {
                                    closeSideNav()
                                    navigateTo("/profile")
                                }}>
                                    <div className={styles.userContainer}>
                                        {
                                            context.user &&
                                            <>

                                                <img src={context.user.picture ?? UserPlaceholder} alt="avatar" className={styles.userAvatar} />
                                                <div className={styles.userName}>{`${context.user.name} ${context.user.surname}`}</div>
                                            </>
                                        }
                                        {
                                            !context.user &&
                                            <>
                                                <Skeleton type="circle" width="32px" height="32px" />
                                                <Skeleton type="rect" width="144px" height="1rem" borderRadius="12px" />
                                            </>
                                        }
                                    </div>
                                </button>
                            </a>
                        </div>
                    </nav>
                    </div>
                )
            }
        </>

    )

}

export default SideNav
